import classNames from 'classnames';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
import React, {useEffect, useCallback, useState, useRef} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {connect} from 'react-redux';
import MediaQuery from 'react-responsive';
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import tabStyles from 'react-tabs/style/react-tabs.css';
import VM from '@scratch/scratch-vm';
import Renderer from '@scratch/scratch-render';
import TherapySoapModal from '../therapy-soap-modal.jsx';
import Blocks from '../../containers/blocks.jsx';
import CostumeTab from '../../containers/costume-tab.jsx';
import TargetPane from '../../containers/target-pane.jsx';
import SoundTab from '../../containers/sound-tab.jsx';
import StageWrapper from '../../containers/stage-wrapper.jsx';
import Loader from '../loader/loader.jsx';
import Box from '../box/box.jsx';
import MenuBar from '../menu-bar/menu-bar.jsx';
import CostumeLibrary from '../../containers/costume-library.jsx';
import BackdropLibrary from '../../containers/backdrop-library.jsx';
import Watermark from '../../containers/watermark.jsx';
import AiCreditsBanner from '../ai-credits/aiCreditsBanner';
import TherapyProjectCreator from '../ai-therapy-project-creator/therapyProjectCreator.jsx';
import Backpack from '../../containers/backpack.jsx';
import ExtensionsButton from '../extension-button/extension-button.jsx';
import WebGlModal from '../../containers/webgl-modal.jsx';
import TipsLibrary from '../../containers/tips-library.jsx';
import Cards from '../../containers/cards.jsx';
import Alerts from '../../containers/alerts.jsx';
import DragLayer from '../../containers/drag-layer.jsx';
import ConnectionModal from '../../containers/connection-modal.jsx';
import TelemetryModal from '../telemetry-modal/telemetry-modal.jsx';

import layout, {STAGE_DISPLAY_SIZES, STAGE_SIZE_MODES, STAGE_DISPLAY_SCALES} from '../../lib/layout-constants';import {resolveStageSize} from '../../lib/screen-utils';
import {colorModeMap} from '../../lib/settings/color-mode/index.js';
import {DEFAULT_THEME, themeMap} from '../../lib/settings/theme/index.js';
import {AccountMenuOptionsPropTypes} from '../../lib/account-menu-options';
import {setFullScreen} from '../../reducers/mode';

import styles from './gui.css';
import codeIcon from './icon--code.svg';
import costumesIcon from './icon--costumes.svg';
import soundsIcon from './icon--sounds.svg';
import DebugModal from '../debug-modal/debug-modal.jsx';
import {setPlatform} from '../../reducers/platform.js';
import {setTheme} from '../../reducers/settings.js';
import {PLATFORM} from '../../lib/platform.js';
import {MenuRefProvider} from '../../contexts/menu-ref-context.jsx';
import {ModalFocusProvider} from '../../contexts/modal-focus-context.jsx';

import {getApp} from 'firebase/app';
import {getFunctions, httpsCallable} from 'firebase/functions';
import {getAuth, onAuthStateChanged} from 'firebase/auth';
import {getFirestore, doc, getDoc} from 'firebase/firestore';

import {
    prepareAIBackdrop,
    prepareAISprite
} from '../../lib/ai-asset-importer.js';


const ariaMessages = defineMessages({
    menuBar: {
        id: 'gui.aria.menuBar',
        defaultMessage: 'Menu topbar',
        description: 'accessibility label for the top menu bar'
    },
    editor: {
        id: 'gui.aria.editor',
        defaultMessage: 'Editor',
        description: 'accessibility label for the main editor area'
    },
    tabList: {
        id: 'gui.aria.tabList',
        defaultMessage: 'Tab list',
        description: 'accessibility label for the editor tab list'
    },
    codePanel: {
        id: 'gui.aria.codePanel',
        defaultMessage: 'Code editor panel',
        description: 'accessibility label for the code editor panel'
    },
    costumesPanel: {
        id: 'gui.aria.costumesPanel',
        defaultMessage: 'Costumes editor panel',
        description: 'accessibility label for the costumes editor panel'
    },
    backdropsPanel: {
        id: 'gui.aria.backdropsPanel',
        defaultMessage: 'Backdrops editor panel',
        description: 'accessibility label for the backdrops editor panel'
    },
    soundsPanel: {
        id: 'gui.aria.soundsPanel',
        defaultMessage: 'Sounds editor panel',
        description: 'accessibility label for the sounds editor panel'
    },
    // backpack: {
    //     id: 'gui.aria.backpack',
    //     defaultMessage: 'Backpack',
    //     description: 'accessibility label for the backpack'
    // },
    stageAndTarget: {
        id: 'gui.aria.stageAndTarget',
        defaultMessage: 'Stage and target',
        description: 'accessibility label for stage and target area'
    },
    stage: {
        id: 'gui.aria.stage',
        defaultMessage: 'Stage',
        description: 'accessibility label for the stage'
    },
    targetPane: {
        id: 'gui.aria.targetPane',
        defaultMessage: 'Target pane',
        description: 'accessibility label for the target pane'
    }
});


const TherapyBuildStep = ({
    label,
    active,
    complete
}) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '16px',
            opacity:
                active || complete ?
                    1 :
                    0.45
        }}
    >
        <div
            style={{
                width: '26px',
                height: '26px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                fontWeight: 700,
                background:
                    complete ?
                        '#e7f7ed' :
                        active ?
                            '#e8f0ff' :
                            '#eeeeee'
            }}
        >
            {complete ? '✓' : active ? '●' : '○'}
        </div>

        <span>
            {label}
        </span>
    </div>
);
// Cache this value to only retrieve it once the first time.
// Assume that it doesn't change for a session.
let isRendererSupported = null;

const GUIComponent = props => {
const intl = useIntl();
const [showSellingActivities, setShowSellingActivities] = useState(false);
const [therapyInfoOpen, setTherapyInfoOpen] = useState(false);
const [editorInfoOpen, setEditorInfoOpen] = useState(false);
const [therapyStudentView, setTherapyStudentView] = useState(false);
const [showAiCreditsBanner, setShowAiCreditsBanner] = useState(false);
const [aiCredits, setAiCredits] = useState(0);
const [
    therapySoapCreditUsage,
    setTherapySoapCreditUsage
] = useState(null);
const [
    freeScratchSoapNotesUsed,
    setFreeScratchSoapNotesUsed
] = useState(0);
const [
    freeScratchAssetGenerationsUsed,
    setFreeScratchAssetGenerationsUsed
] = useState(0);
const [
    freeScratchBackdropsUsed,
    setFreeScratchBackdropsUsed
] = useState(0);
const [mobileClinicianTools, setMobileClinicianTools] = useState(false);
const [isTherapyMode, setIsTherapyMode] = useState(false);
const [allowStageDragging, setAllowStageDragging] = useState(true);
const [isPurchasedCodeBuilding, setIsPurchasedCodeBuilding] = useState(false);
const [therapyActivityStarted, setTherapyActivityStarted] = useState(false);
const [therapyBuildStage, setTherapyBuildStage] = useState('');
const [
    showTherapyProjectCreator,
    setShowTherapyProjectCreator
] = useState(false);
const [therapyPrompt, setTherapyPrompt] = useState('');
const [therapyAgeRange, setTherapyAgeRange] = useState('6-8');
const [therapyItemCount, setTherapyItemCount] = useState(6);
const [therapyDifficulty, setTherapyDifficulty] = useState('auto');
const [therapyRoundCount, setTherapyRoundCount] = useState(3);
const [therapyInterests, setTherapyInterests] = useState('');

const [therapyActivity, setTherapyActivity] = useState(null);
const [therapyLoading, setTherapyLoading] = useState(false);
const [therapyError, setTherapyError] = useState('');
const [therapyStep, setTherapyStep] = useState(0);

const [therapyRoundData, setTherapyRoundData] = useState([]);

const [therapySoapOpen, setTherapySoapOpen] = useState(false);
const [therapySoapNote, setTherapySoapNote] = useState('');
const [therapySoapLoading, setTherapySoapLoading] = useState(false);
const [therapySoapError, setTherapySoapError] = useState('');

const [purchasedProjectHasCode, setPurchasedProjectHasCode] = useState(false);

// const therapyStageContainerRef = useRef(null);
const isNarrowTherapy =
    typeof document !== 'undefined' &&
    document.documentElement.clientWidth < 1100;


const [
    freeScratchProjectsUsed,
    setFreeScratchProjectsUsed
] = useState(0);

      const therapyProjectState = {
        prompt: therapyPrompt,
        ageRange: therapyAgeRange,
        itemCount: therapyItemCount,
        difficulty: therapyDifficulty,
        roundCount: therapyRoundCount,
        interests: therapyInterests,
        activity: therapyActivity,
        roundData: therapyRoundData,
        step: therapyStep,
        activityStarted: therapyActivityStarted
    };

    const handleRestoreTherapyProject = therapy => {
    setTherapyPrompt(therapy.prompt || '');
    setTherapyAgeRange(therapy.ageRange || '6-8');
    setTherapyItemCount(therapy.itemCount || 6);
    setTherapyDifficulty(therapy.difficulty || 'auto');
    setTherapyRoundCount(therapy.roundCount || 3);
    setTherapyInterests(therapy.interests || '');

    setTherapyActivity(therapy.activity || null);
    setTherapyRoundData(therapy.roundData || []);
    setTherapyStep(therapy.step || 0);
    setTherapyActivityStarted(Boolean(therapy.activityStarted));

     setIsTherapyMode(true);
};

useEffect(() => {
    let cancelled = false;
    let timer = null;

    const checkForTherapyProject = () => {
        if (cancelled) {
            return;
        }

        const storedTherapyState =
            sessionStorage.getItem('therapyProjectState');

        if (!storedTherapyState) {
            timer = setTimeout(
                checkForTherapyProject,
                200
            );

            return;
        }

        try {
            const therapy =
                JSON.parse(storedTherapyState);

            console.log(
                '[THERAPY] Restoring Therapy project from Alyd Market:',
                therapy
            );

            handleRestoreTherapyProject(therapy);

            sessionStorage.removeItem(
                'therapyProjectState'
            );
        } catch (error) {
            console.error(
                '[THERAPY] Failed to restore Therapy project state:',
                error
            );

            sessionStorage.removeItem(
                'therapyProjectState'
            );
        }
    };

    checkForTherapyProject();

    return () => {
        cancelled = true;

        if (timer) {
            clearTimeout(timer);
        }
    };
}, []);


const handleCreateTherapyActivity = async () => {
    const cleanPrompt = therapyPrompt.trim();

    if (!cleanPrompt) return;

    setTherapyLoading(true);
    setTherapyError('');
    setTherapyBuildStage('planning');

    /*
     * One requestId represents this entire Therapy Project attempt.
     *
     * The same ID is used to:
     * - reserve the free use / credits
     * - complete the reservation
     * - release the reservation if generation fails
     */
    const requestId =
        `${Date.now()}_${Math.random().toString(36).slice(2)}`;

    let therapyProjectReserved = false;
    let therapyProjectCompleted = false;

    let functions = null;
    let releaseTherapyProject = null;

    try {
        functions = getFunctions(
            getApp(),
            'us-central1'
        );

        /*
         * STEP 1
         * Reserve either:
         *
         * - one of the 2 free Therapy Projects
         * OR
         * - 5 global AI credits
         */
        const reserveTherapyProject = httpsCallable(
            functions,
            'reserveTherapyProject'
        );

        const reserveResult =
            await reserveTherapyProject({
                requestId
            });

        therapyProjectReserved = true;

        console.log(
            '[THERAPY] Full reserve response:',
            reserveResult.data
        );

        console.log(
            '[THERAPY] Project AI usage reserved:',
            reserveResult.data?.creditUsage
        );

        /*
         * Prepare the release callable now that we know
         * a reservation exists.
         */
        releaseTherapyProject = httpsCallable(
            functions,
            'releaseTherapyProject'
        );

        /*
         * STEP 2
         * Generate the Therapy Activity plan.
         */
        const generateTherapyActivityPlan = httpsCallable(
            functions,
            'generateTherapyActivityPlan'
        );

        const result = await generateTherapyActivityPlan({
            prompt: cleanPrompt,
            ageRange: therapyAgeRange,
            itemCount: therapyItemCount,
            difficulty: therapyDifficulty,
            roundCount: therapyRoundCount,
            interests: therapyInterests.trim()
        });

        const activity = result.data?.activity;

        if (
            !activity ||
            !activity.title ||
            !activity.goal ||
            !activity.backdropPrompt ||
            !Array.isArray(activity.rounds) ||
            activity.rounds.length !== therapyRoundCount ||
            !Array.isArray(activity.sprites) ||
            activity.sprites.length !== therapyItemCount
        ) {
            throw new Error(
                'The activity returned by AI was incomplete.'
            );
        }

        const spritePlans = Array.isArray(activity.sprites) ?
            activity.sprites :
            [];

        if (spritePlans.length === 0) {
            throw new Error(
                'The activity did not include any usable sprites.'
            );
        }

        setTherapyBuildStage('assets');

        /*
         * STEP 3
         * Generate the included Therapy Project assets.
         *
         * These assets are part of the Therapy Project price.
         * They do NOT use Character/Costume or Backdrop credits.
         */
        const generateTherapyAssets = httpsCallable(
            functions,
            'generateTherapyAssets'
        );

        console.log(
            '[THERAPY] Requesting therapy assets:',
            {
                backdropPrompt: activity.backdropPrompt,
                sprites: spritePlans.map(sprite => sprite.name)
            }
        );

        const assetResult = await generateTherapyAssets({
            backdropPrompt: activity.backdropPrompt,
            sprites: spritePlans.map(sprite => ({
                name: sprite.name,
                prompt: sprite.prompt
            }))
        });

        const backdropImageBase64 =
            assetResult.data?.backdropImageBase64;

        const generatedAssetSprites =
            assetResult.data?.sprites || [];

        if (!backdropImageBase64) {
            throw new Error(
                'No backdrop image was returned.'
            );
        }

        if (
            !Array.isArray(generatedAssetSprites) ||
            generatedAssetSprites.length === 0
        ) {
            throw new Error(
                'No therapy sprites were returned.'
            );
        }

        console.log(
            '[THERAPY] Therapy assets returned:',
            {
                backdrop: Boolean(backdropImageBase64),
                spriteCount: generatedAssetSprites.length
            }
        );

        /*
         * STEP 4
         * Build the generated Therapy Project in the VM.
         */
        setTherapyBuildStage('building');

        const storage = vm.runtime.storage;

        const vmCostumes = await prepareAIBackdrop(
            backdropImageBase64,
            storage
        );

        const stageTarget = vm.runtime.getTargetForStage();

        if (!stageTarget) {
            throw new Error(
                'Could not find the Scratch stage.'
            );
        }

        vm.setEditingTarget(stageTarget.id);

        await Promise.all(
            vmCostumes.map(backdrop =>
                vm.addBackdrop(
                    backdrop.md5,
                    backdrop
                )
            )
        );

        console.log(
            '[THERAPY] AI backdrop added.'
        );

        const generatedSprites = await Promise.all(
            generatedAssetSprites.map(
                async (generatedAsset, i) => {
                    if (!generatedAsset?.imageBase64) {
                        throw new Error(
                            `No image was returned for ${
                                generatedAsset?.name ||
                                `sprite ${i + 1}`
                            }.`
                        );
                    }

                    console.log(
                        `[THERAPY] Preparing sprite ${
                            i + 1
                        } of ${
                            generatedAssetSprites.length
                        }:`,
                        generatedAsset.name || 'AI Sprite'
                    );

                    const newSprite = await prepareAISprite(
                        generatedAsset.imageBase64,
                        storage,
                        generatedAsset.name ||
                            `AI Sprite ${i + 1}`
                    );

                    console.log(
                        `[THERAPY] Sprite ${
                            i + 1
                        } prepared:`,
                        generatedAsset.name || 'AI Sprite'
                    );

                    return {
                        name:
                            generatedAsset.name ||
                            `AI Sprite ${i + 1}`,
                        sprite: newSprite
                    };
                }
            )
        );

        if (generatedSprites.length === 0) {
            throw new Error(
                'No usable sprites were generated.'
            );
        }

        const existingSprites = vm.runtime.targets.filter(
            target => !target.isStage
        );

        console.log(
            '[THERAPY] Existing sprites:',
            existingSprites.map(target => ({
                id: target.id,
                name: target.getName()
            }))
        );

        await Promise.all(
            existingSprites.map(target =>
                Promise.resolve(
                    vm.deleteSprite(target.id)
                )
            )
        );

        console.log(
            '[THERAPY] Existing sprites deleted. Targets now:',
            vm.runtime.targets.map(target => ({
                id: target.id,
                name: target.getName(),
                isStage: target.isStage
            }))
        );

        const spriteCount = generatedSprites.length;

        let spriteSize;
        let columns;

        if (spriteCount <= 3) {
            spriteSize = 95;
            columns = spriteCount;
        } else if (spriteCount <= 4) {
            spriteSize = 82;
            columns = 2;
        } else if (spriteCount <= 6) {
            spriteSize = 72;
            columns = 3;
        } else if (spriteCount <= 8) {
            spriteSize = 60;
            columns = 4;
        } else {
            spriteSize = 52;
            columns = 5;
        }

        const rows = Math.ceil(
            spriteCount / columns
        );

        const xSpacing = {
            1: 0,
            2: 180,
            3: 150,
            4: 115,
            5: 90
        }[columns];

        const ySpacing =
            rows > 1 ? 160 : 0;

        const spritePositions = [];

        for (
            let row = 0;
            row < rows;
            row++
        ) {
            const startIndex =
                row * columns;

            const itemsInRow = Math.min(
                columns,
                spriteCount - startIndex
            );

            const rowWidth =
                (itemsInRow - 1) * xSpacing;

            const startX =
                -(rowWidth / 2);

            const y =
                rows === 1 ?
                    0 :
                    ((rows - 1) * ySpacing) / 2 -
                        (row * ySpacing);

            for (
                let column = 0;
                column < itemsInRow;
                column++
            ) {
                spritePositions.push({
                    x:
                        startX +
                        (column * xSpacing),
                    y
                });
            }
        }

        console.log(
            '[THERAPY] Sprite layout:',
            {
                spriteCount,
                spriteSize,
                columns,
                rows,
                positions: spritePositions
            }
        );

        for (
            let i = 0;
            i < generatedSprites.length;
            i++
        ) {
            const generatedSprite =
                generatedSprites[i];

            console.log(
                `[THERAPY] Adding sprite ${
                    i + 1
                } of ${
                    generatedSprites.length
                }:`,
                generatedSprite.name
            );

            await vm.addSprite(
                generatedSprite.sprite
            );

            const addedSprites =
                vm.runtime.targets.filter(
                    target => !target.isStage
                );

            const addedSprite =
                addedSprites[
                    addedSprites.length - 1
                ];

            if (addedSprite) {
                const position =
                    spritePositions[i];

                addedSprite.setXY(
                    position.x,
                    position.y
                );

                addedSprite.setSize(
                    spriteSize
                );

                console.log(
                    '[THERAPY] Sprite positioned and sized:',
                    generatedSprite.name,
                    {
                        position,
                        size: spriteSize
                    }
                );
            }

            console.log(
                '[THERAPY] AI sprite added:',
                generatedSprite.name
            );
        }

        console.log(
            '[THERAPY] All AI sprites added. Targets now:',
            vm.runtime.targets.map(target => ({
                id: target.id,
                name: target.getName(),
                isStage: target.isStage
            }))
        );

        /*
         * Preserve existing Therapy Activity state setup.
         */
        setTherapyActivity(activity);
        setTherapyStep(0);

        setTherapyRoundData(
            activity.rounds.map(() => ({
                plus: 0,
                minus: 0,
                note: ''
            }))
        );

        /*
         * STEP 5
         * The entire Therapy Project was successfully generated
         * and built.
         *
         * Finalize the existing reservation.
         *
         * This does NOT deduct another 5 credits.
         */
        const completeTherapyProject = httpsCallable(
            functions,
            'completeTherapyProject'
        );

        const completeResult =
            await completeTherapyProject({
                requestId
            });

        therapyProjectCompleted = true;

        console.log(
            '[THERAPY] Full completion response:',
            completeResult.data
        );

        const creditUsage =
    completeResult.data?.creditUsage || null;

console.log(
    '[THERAPY] Project AI usage completed:',
    creditUsage
);

/*
 * Update the local AI balance/banner state using the
 * authoritative values returned by the backend.
 */
if (typeof creditUsage?.aiCredits === 'number') {
    setAiCredits(creditUsage.aiCredits);
}

if (
    typeof creditUsage?.freeScratchProjectsUsed === 'number'
) {
    setFreeScratchProjectsUsed(
        creditUsage.freeScratchProjectsUsed
    );
}
if (
    typeof creditUsage?.freeScratchSoapNotesUsed === 'number'
) {
    setFreeScratchSoapNotesUsed(
        creditUsage.freeScratchSoapNotesUsed
    );
}

setTherapyActivityStarted(true);

        console.log(
            '[THERAPY] Returning creditUsage to modal:',
            creditUsage
        );

        return creditUsage;
    } catch (err) {
        console.error(
            'Therapy activity generation error:',
            err
        );

        /*
         * If a reservation was successfully created but the
         * project was not completed, restore exactly what was
         * reserved.
         */
        if (
            therapyProjectReserved &&
            !therapyProjectCompleted &&
            releaseTherapyProject
        ) {
            try {
                const releaseResult =
                    await releaseTherapyProject({
                        requestId,
                        failureMessage:
                            err.message ||
                            'Therapy Project generation failed.'
                    });

                console.log(
                    '[THERAPY] Project AI usage released:',
                    releaseResult.data?.creditUsage
                );
            } catch (releaseError) {
                console.error(
                    'CRITICAL: Therapy Project reservation ' +
                    'release failed:',
                    releaseError
                );
            }
        }

        setTherapyError(
            err.message ||
            'Could not create the activity.'
        );

        throw err;
    } finally {
        setTherapyLoading(false);
        setTherapyBuildStage('');
    }
};






    const {
        accountMenuOptions,
        onSetFullScreen,
        activeTabIndex,
        alertsVisible,
        authorId,
        authorThumbnailUrl,
        authorUsername,
        authorAvatarBadge,
        basePath,
        backdropLibraryVisible,
        backpackConfigured,
        backpackHost,
        backpackVisible,
        blocksId,
        blocksTabVisible,
        cardsVisible,
        canChangeLanguage,
        canChangeColorMode,
        canChangeTheme,
        canCreateNew,
        canEditTitle,
        canManageFiles,
        canRemix,
        canSave,
        canCreateCopy,
        canShare,
        canUseCloud,
        children,
        connectionModalVisible,
        costumeLibraryVisible,
        costumesTabVisible,
        debugModalVisible,
        onDebugModalClose,
        onTutorialSelect,
        enableCommunity,
        hasActiveMembership,
        isCreating,
        isFetchingUserData,
        isViewerMode,
        isFullScreen,
        isPlayerOnly,
        isRtl,
        isShared,
        isTelemetryEnabled,
        isTotallyNormal,
        loading,
        logo,
        manuallySaveThumbnails,
        onSetManualThumbnail,
        onSetManualThumbnailButtonClick,
        menuBarHidden,
        renderLogin,
        onClickAbout,
        onLogOut,
        onClickLogin,
        onOpenRegistration,
        onToggleLoginOpen,
        onActivateCostumesTab,
        onActivateSoundsTab,
        onActivateTab,
        onClickLogo,
        onExtensionButtonClick,
        onNewSpriteClick,
        onNewLibraryCostumeClick,
        onNewLibraryBackdropClick,
        onProjectTelemetryEvent,
        onRequestCloseBackdropLibrary,
        onRequestCloseCostumeLibrary,
        onRequestCloseDebugModal,
        onRequestCloseTelemetryModal,
        onSeeCommunity,
        onShare,
        onShowPrivacyPolicy,
        onStartSelectingFileUpload,
        onTelemetryModalCancel,
        onTelemetryModalOptIn,
        onTelemetryModalOptOut,
        onUpdateProjectThumbnail,
        showComingSoon,
        showNewFeatureCallouts,
        soundsTabVisible,
        stageSizeMode,
        targetIsStage,
        telemetryModalVisible,
        colorMode,
        theme,
        tipsLibraryVisible,
        useExternalPeripheralList,
        username,
        avatarBadge,
        userOwnsProject,
        hideTutorialProjects,
        vm,
        ...componentProps
    } = omit(props, 'dispatch', 'setPlatform');
    if (children) {
        return <Box {...componentProps}>{children}</Box>;
    }

    useEffect(() => {
        if (props.platform) {
            // TODO: This uses the imported `setPlatform` directly,
            // but it should probably use the dispatched version from props.
            setPlatform(props.platform);
        }
    }, [props.platform]);

    useEffect(() => {
        if (
            !isFetchingUserData &&
            !themeMap[theme]?.isAvailable?.({hasActiveMembership})
        ) {
            // If the preferred theme is not available, fall back to default.
            // TODO: It would be cleaner to do this on redux init.
            props.setTheme(DEFAULT_THEME);
        }
    }, [theme, hasActiveMembership, props.setTheme]);

    const tabClassNames = {
        tabs: styles.tabs,
        tab: classNames(tabStyles.reactTabsTab, styles.tab),
        tabList: classNames(tabStyles.reactTabsTabList, styles.tabList),
        tabPanel: classNames(tabStyles.reactTabsTabPanel, styles.tabPanel),
        tabPanelSelected: classNames(tabStyles.reactTabsTabPanelSelected, styles.isSelected),
        tabSelected: classNames(tabStyles.reactTabsTabSelected, styles.isSelected)
    };

 

useEffect(() => {
    const app = getApp();
    const auth = getAuth(app);
    const db = getFirestore(app);

    const unsubscribe = onAuthStateChanged(auth, async user => {
    if (!user) {
    setAiCredits(0);
    setFreeScratchAssetGenerationsUsed(0);
    setFreeScratchBackdropsUsed(0);
    setFreeScratchProjectsUsed(0);
    setFreeScratchSoapNotesUsed(0);
    return;
}

        try {
            const uid = user.uid;

            const [companySnap, profileSnap] = await Promise.all([
                getDoc(doc(db, 'companies', uid)),
                getDoc(doc(db, 'profiles', uid))
            ]);

            let userData = null;

            if (companySnap.exists()) {
                userData = companySnap.data();
            } else if (profileSnap.exists()) {
                userData = profileSnap.data();
            }

            const currentCredits = Number(
                userData?.aiCredits || 0
            );

            const currentFreeGenerationsUsed = Number(
                userData?.freeScratchAssetGenerationsUsed || 0
            );

          const currentFreeBackdropsUsed = Number(
    userData?.freeScratchBackdropsUsed || 0
);

const currentFreeProjectsUsed = Number(
    userData?.freeScratchProjectsUsed || 0
);

const currentFreeSoapNotesUsed = Number(
    userData?.freeScratchSoapNotesUsed || 0
);

setAiCredits(currentCredits);

            setFreeScratchAssetGenerationsUsed(
                currentFreeGenerationsUsed
            );

            setFreeScratchBackdropsUsed(
                currentFreeBackdropsUsed
            );
            setFreeScratchProjectsUsed(
    currentFreeProjectsUsed
);
setFreeScratchSoapNotesUsed(
    currentFreeSoapNotesUsed
);
        } catch (error) {
    console.error(
        'Failed to load AI credit information:',
        error
    );

    setAiCredits(0);
    setFreeScratchAssetGenerationsUsed(0);
    setFreeScratchBackdropsUsed(0);
    setFreeScratchProjectsUsed(0);
    setFreeScratchSoapNotesUsed(0);
}
    });

    return () => {
        unsubscribe();
    };
}, []);

    const onCloseDebugModal = useCallback(() => {
        if (onDebugModalClose) {
            onDebugModalClose();
        }
        onRequestCloseDebugModal();
    }, [onDebugModalClose, onRequestCloseDebugModal]);

    if (isRendererSupported === null) {
        isRendererSupported = Renderer.isSupported();
    }

const handleGenerateTherapySoap = async () => {
    if (!therapyActivity) return;

    
    setTherapySoapLoading(true);
    setTherapySoapError('');
    setTherapySoapNote('');
    setTherapySoapCreditUsage(null);

    try {
        const functions = getFunctions(
            getApp(),
            'us-central1'
        );

        const generateSoapNote = httpsCallable(
            functions,
            'generateTherapySOAPNote'
        );

        const requestId =
            `${Date.now()}_${Math.random()
                .toString(36)
                .slice(2)}`;

        const result = await generateSoapNote({
            ageRange: therapyAgeRange,
            target: therapyPrompt,
            activityTitle: therapyActivity.title || '',
            difficulty: therapyDifficulty,
            rounds: therapyActivity.rounds || [],
            roundData: therapyRoundData,
            requestId
        });

        setTherapySoapNote(
            result.data.note || ''
        );

        const creditUsage =
            result.data?.creditUsage || null;

        setTherapySoapCreditUsage(
            creditUsage
        );

        if (
            typeof creditUsage?.aiCredits === 'number'
        ) {
            setAiCredits(
                creditUsage.aiCredits
            );
        }

        if (
            typeof creditUsage?.freeScratchSoapNotesUsed ===
            'number'
        ) {
            setFreeScratchSoapNotesUsed(
                creditUsage.freeScratchSoapNotesUsed
            );
        }
    } catch (error) {
        console.error(
            'SOAP note generation failed:',
            error
        );

        setTherapySoapError(
            error?.message ||
            'Could not generate the SOAP note.'
        );
    } finally {
        setTherapySoapLoading(false);
    }
};

useEffect(() => {
    const handlePurchasedProjectCodeCheck = event => {
        const hasCode =
            Boolean(event.detail?.hasCode);

        console.log(
            '[GUI] Purchased project has code:',
            hasCode
        );

        setPurchasedProjectHasCode(hasCode);
    };

    window.addEventListener(
        'alyd-purchased-project-code-check',
        handlePurchasedProjectCodeCheck
    );

    return () => {
        window.removeEventListener(
            'alyd-purchased-project-code-check',
            handlePurchasedProjectCodeCheck
        );
    };
}, []);

const handleSellingActivitiesClick = () => {
    setShowSellingActivities(true);
};


// Space taken by menu bar, therapy toolbar, stage header, and padding.
// Tune these two numbers if tablets look slightly off.
const pickStudentStageSize = () => {
    const w = window.innerWidth;

    // console.log(
    //     'STUDENT VIEW WIDTH:',
    //     w
    // );

    /*
     * Student View sizing based on viewport width.
     *
     * Large desktop / HD: 2.2 = 1056x792
     * Desktop / laptop:   1.8 = 864x648
     * Tablet:            1.4 = 672x504
     * Small / phone:     1.1 = 528x396
     */

    // Large desktop / HD monitor
    if (w >= 1600) {
        return STAGE_DISPLAY_SIZES.therapyStudentLarge;
    }

    // Normal laptop / desktop
    if (w >= 1100) {
        return STAGE_DISPLAY_SIZES.therapyStudent;
    }

    // iPad / tablet
    if (w >= 700) {
        return STAGE_DISPLAY_SIZES.therapyStudentTablet;
    }

    // Phone / very small viewport
    return STAGE_DISPLAY_SIZES.therapyStudentSmall;
};

const [studentStageSize, setStudentStageSize] = useState(
    () => (typeof window === 'undefined' ?
        STAGE_DISPLAY_SIZES.therapyStudent :
        pickStudentStageSize())
);

useEffect(() => {
    const onResize = () => {
        setStudentStageSize(pickStudentStageSize());
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    return () => {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
    };
}, []);

return (<MediaQuery minWidth={layout.fullSizeMinWidth}>{isFullSize => {
    const stageSize = resolveStageSize(stageSizeMode, isFullSize);
    const boxStyles = classNames(styles.bodyWrapper, {
        [styles.bodyWrapperWithoutMenuBar]: menuBarHidden
    });
       
   return isPlayerOnly && !isPurchasedCodeBuilding ? (
    <StageWrapper
        onToggleStageDragging={() => {
            setAllowStageDragging(current => !current);
        }}
        isPurchasedCodeBuilding={isPurchasedCodeBuilding}
        onTogglePurchasedCodeBuilding={() => {
            setIsTherapyMode(false);
            setIsPurchasedCodeBuilding(true);
        }}
        allowStageDragging={allowStageDragging}
        isFullScreen={isFullScreen}
        isViewerMode={isViewerMode}
        isRendererSupported={isRendererSupported}
        isRtl={isRtl}
        loading={loading}
        stageSize={STAGE_SIZE_MODES.large}
        vm={vm}
    >
        {alertsVisible ? (
            <Alerts className={styles.alertsContainer} />
        ) : null}
    </StageWrapper>
) : (
    <ModalFocusProvider>
        <Box
            className={styles.pageWrapper}
            dir={isRtl ? 'rtl' : 'ltr'}
            {...componentProps}
        >
            {telemetryModalVisible ? (
                <TelemetryModal
                    isRtl={isRtl}
                    isTelemetryEnabled={isTelemetryEnabled}
                    onCancel={onTelemetryModalCancel}
                    onOptIn={onTelemetryModalOptIn}
                    onOptOut={onTelemetryModalOptOut}
                    onRequestClose={onRequestCloseTelemetryModal}
                    onShowPrivacyPolicy={onShowPrivacyPolicy}
                />
            ) : null}

            {loading ? (
                <Loader />
            ) : null}

            {isCreating ? (
                <Loader messageId="gui.loader.creating" />
            ) : null}

            {isRendererSupported ? null : (
                <WebGlModal isRtl={isRtl} />
            )}

            {tipsLibraryVisible ? (
                <TipsLibrary
                    hideTutorialProjects={hideTutorialProjects}
                    onTutorialSelect={onTutorialSelect}
                />
            ) : null}

            {cardsVisible ? (
                <Cards />
            ) : null}

            {alertsVisible ? (
                <Alerts className={styles.alertsContainer} />
            ) : null}

            {connectionModalVisible ? (
                <ConnectionModal
                    useExternalPeripheralList={useExternalPeripheralList}
                    vm={vm}
                />
            ) : null}

            {costumeLibraryVisible ? (
                <CostumeLibrary
                    vm={vm}
                    onRequestClose={onRequestCloseCostumeLibrary}
                />
            ) : null}

            <DebugModal
                isOpen={debugModalVisible}
                onClose={onCloseDebugModal}
            />

            {backdropLibraryVisible ? (
                <BackdropLibrary
                    vm={vm}
                    onRequestClose={onRequestCloseBackdropLibrary}
                />
            ) : null}

            {/* TODO - in case of moving MenuRefProvider which seems likely,
            make sure to move it from tests as well */}
            {!menuBarHidden &&
                !isPurchasedCodeBuilding &&
                !(isViewerMode && isTherapyMode) && (
                <MenuRefProvider>
                    <MenuBar
                        ariaRole="banner"
                        onRestoreTherapyProject={handleRestoreTherapyProject}
                        therapyProjectState={therapyProjectState}
                        ariaLabel={intl.formatMessage(ariaMessages.menuBar)}
                        authorId={authorId}
                        authorThumbnailUrl={authorThumbnailUrl}
                        authorUsername={authorUsername}
                        authorAvatarBadge={authorAvatarBadge}
                        canChangeLanguage={canChangeLanguage}
                        canChangeColorMode={canChangeColorMode}
                        canChangeTheme={canChangeTheme}
                        canCreateCopy={canCreateCopy}
                        canCreateNew={canCreateNew}
                        canEditTitle={canEditTitle}
                        canManageFiles={canManageFiles}
                        canRemix={canRemix}
                        canSave={canSave}
                        canShare={canShare}
                        className={styles.menuBarPosition}
                        enableCommunity={enableCommunity}
                        hasActiveMembership={hasActiveMembership}
                        isShared={isShared}
                        isTotallyNormal={isTotallyNormal}
                        logo={logo}
                        renderLogin={renderLogin}
                        showComingSoon={showComingSoon}
                        onClickAbout={onClickAbout}
                        onClickLogo={onClickLogo}
                        onLogOut={onLogOut}
                        onClickLogin={onClickLogin}
                        onOpenRegistration={onOpenRegistration}
                        onProjectTelemetryEvent={onProjectTelemetryEvent}
                        onSeeCommunity={onSeeCommunity}
                        onShare={onShare}
                        onStartSelectingFileUpload={onStartSelectingFileUpload}
                        onToggleLoginOpen={onToggleLoginOpen}
                        userOwnsProject={userOwnsProject}
                        username={username}
                        avatarBadge={avatarBadge}
                        accountMenuOptions={accountMenuOptions}
                    />
                </MenuRefProvider>
            )}

            {isViewerMode && isTherapyMode && (
   <div
    style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '14px',
        padding: '8px',
        background: '#f2f2f2',
        borderBottom: '1px solid #ddd',
        flexWrap: 'wrap'
    }}
>
    
        <button
            type="button"
            onClick={() => {
                setAllowStageDragging(current => !current);
            }}
            style={{
                whiteSpace: 'nowrap',
                flexShrink: 0,
                padding: '8px 18px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                cursor: 'pointer',
                fontWeight: allowStageDragging ? 'bold' : 'normal'
            }}
        >
            Allow Dragging: {allowStageDragging ? 'On' : 'Off'}
        </button>

{isTherapyMode && (
        <button
    type="button"
    onClick={() => {
        setTherapyStudentView(current => !current);
    }}
    style={{
        whiteSpace: 'nowrap',
        flexShrink: 0,
        padding: '8px 18px',
        borderRadius: '8px',
        border: '1px solid #ccc',
        cursor: 'pointer',
        fontWeight: therapyStudentView ? 'bold' : 'normal'
    }}
>
    {therapyStudentView ? 'Clinician View' : 'Student View'}
</button>
)}
         {!purchasedProjectHasCode && (
            <button
                type="button"
                onClick={() => {
                    setIsTherapyMode(false);
                    setIsPurchasedCodeBuilding(true);
                }}
                style={{
                    padding: '8px 18px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                }}
            >
                Code Building
            </button>
        )}
    </div>
    
)}

            {isPurchasedCodeBuilding && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '8px',
                        background: '#f2f2f2',
                        borderBottom: '1px solid #ddd'
                    }}
                >
                    <button
                        type="button"
                        onClick={() => {
                            if (therapyActivity) {
                                setIsTherapyMode(true);
                                setIsPurchasedCodeBuilding(false);
                            } else {
                                onSetFullScreen(true);
                                setIsPurchasedCodeBuilding(false);
                            }
                        }}
                        style={{
                            padding: '8px 18px',
                            borderRadius: '8px',
                            border: '1px solid #ccc',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        Viewer
                    </button>
                </div>
            )}
{!isViewerMode && !isPurchasedCodeBuilding && (
    <div
        style={{
            backgroundColor: '#f4f1f8',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            boxSizing: 'border-box'
        }}
    >
        <button
            style={{
                fontSize: '12px',
                padding: '5px 10px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                marginLeft: '4px'
            }}
            onClick={() => {
                window.location.href = 'https://alydmarket.com/dashboard';
            }}
        >
            ← Alyd Market
        </button>

        <button
            style={{
                fontSize: '12px',
                padding: '5px 10px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                marginLeft: '8px'
            }}
            onClick={handleSellingActivitiesClick}
        >
            Buying and Selling
        </button>
    </div>
)}
             

        {!isViewerMode && !isPurchasedCodeBuilding && (
    <div
       style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    background: '#f2f2f2',
    borderBottom: '1px solid #ddd',
    flexWrap: 'wrap'
}}
    >

        <button
    type="button"
    onClick={() => {
        if (isTherapyMode) {
            setTherapyInfoOpen(true);
        } else {
            setEditorInfoOpen(true);
        }
    }}
    style={{
        padding: '8px 18px',
        borderRadius: '8px',
        border: '1px solid #ccc',
        background: 'white',
        cursor: 'pointer',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        flexShrink: 0
    }}
>
    {isTherapyMode ? 'Therapy Info' : 'Editor Info'}
   
</button>
        {/* AI Credits - positioned independently */}
     <button
    type="button"
    onClick={() => {
        setShowAiCreditsBanner(current => !current);
    }}
    style={{
        // position: 'absolute',
        // left: '27%',
        padding: '8px 18px',
        borderRadius: '8px',
        border: '1px solid #7c5ce7',
        background: '#ffffff',
        color: '#6b46d9',
        cursor: 'pointer',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '7px'
    }}
>
    <span>
        ✨ AI Credits
    </span>

    <span
        style={{
            minWidth: '22px',
            padding: '2px 7px',
            borderRadius: '12px',
            background: '#ede9fe',
            color: '#5b21b6',
            fontSize: '12px',
            fontWeight: 'bold',
            textAlign: 'center'
        }}
    >
        {aiCredits}
    </span>
</button>

        {/* These two stay centered */}
        <button
            type="button"
            onClick={() => setIsTherapyMode(false)}
            style={{
                padding: '8px 18px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                cursor: 'pointer',
                fontWeight: isTherapyMode ? 'normal' : 'bold'
            }}
        >
            Editor
        </button>

        <button
            type="button"
            onClick={() => setIsTherapyMode(true)}
            style={{
                padding: '8px 18px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                cursor: 'pointer',
                fontWeight: isTherapyMode ? 'bold' : 'normal'
            }}
        >
            Therapy
        </button>

        {/* Therapy-only button - positioned independently */}
        {isTherapyMode && (
            <button
                type="button"
                onClick={() => {
                    setAllowStageDragging(current => !current);
                }}
                style={{
                    // position: 'absolute',
                    // left: 'calc(50% + 115px)',
                    padding: '8px 18px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    fontWeight: allowStageDragging ? 'bold' : 'normal'
                }}
            >
                Allow Dragging: {allowStageDragging ? 'On' : 'Off'}
            </button>
            
            
            
        )}
      {isTherapyMode && (
<button
    type="button"
    onClick={() => {
        setTherapyStudentView(current => !current);
    }}
    style={{
        padding: '8px 18px',
        borderRadius: '8px',
        border: '1px solid #ccc',
        cursor: 'pointer',
        fontWeight: therapyStudentView ? 'bold' : 'normal'
    }}
>
    {therapyStudentView ? 'Clinician View' : 'Student View'}
</button>
)}

    </div>
)}




{(therapyInfoOpen || editorInfoOpen) && (
    <>
     <div style={{
    margin: '10px 32px 12px',
    padding: '11px 20px',
    borderRadius: '8px',
    background: '#f8fafc',
    border: '1px solid #d8dee8',
    color: '#475569',
    fontSize: '12px',
    lineHeight: 1.4
}}>
  
    <div
        style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}
        onClick={() => {
            setTherapyInfoOpen(false);
            setEditorInfoOpen(false);
        }}
    >
        <div
            style={{
                width: '100%',
                maxWidth: '600px',
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxSizing: 'border-box',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
            }}
            onClick={e => e.stopPropagation()}
        >
            {therapyInfoOpen ? (
                <>
                    <h2
                        style={{
                            marginTop: 0,
                            marginBottom: '12px'
                        }}
                    >
                        Therapy
                    </h2>

                    <p
                        style={{
                            margin: 0,
                            lineHeight: 1.5
                        }}
                    >
                        Generate and save a complete AI therapy activity and
                        plan, including characters, adding variations of characters with costumes to use while coding,  and a backdrop. Afterwards,
                        you can return to Editor to customize the activity by
                        adding additional characters, changing the backdrop,
                        and adding code.
                    </p>

                    <p
                        style={{
                            marginTop: '16px',
                            marginBottom: 0,
                            lineHeight: 1.5,
                            fontWeight: 700
                        }}
                    >
                        Important: Starting a new Therapy activity will replace
                        the current characters and backdrop already created in
                        Editor or from a previous Therapy-generated activity.
                    </p>
                </>
            ) : (
                <>
                    <h2
                        style={{
                            marginTop: 0,
                            marginBottom: '12px'
                        }}
                    >
                        Editor
                    </h2>

                    <p
                        style={{
                            margin: 0,
                            lineHeight: 1.5
                        }}
                    >
                        Create and save your own projects in Editor. Add
                        characters. variations of characters with costumes to use while coding, and backdrops by uploading them from your PC,
                        selecting them from your dashboard library, or generating
                        them with AI. The Editor also includes drag-and-drop code
                        building to create your own unique activities. You can
                        also use Editor to customize a Therapy-generated activity
                        without losing the therapy plan.
                    </p>
                </>
            )}

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '20px'
                }}
            >
                <button
                    type="button"
                    onClick={() => {
                        setTherapyInfoOpen(false);
                        setEditorInfoOpen(false);
                    }}
                    style={{
                        padding: '8px 18px',
                        borderRadius: '8px',
                        border: '1px solid #ccc',
                        background: 'white',
                        cursor: 'pointer',
                        fontWeight: 600
                    }}
                >
                    Close
                </button>
            </div>
        </div>
    </div>

         </div>
    </>
)}
{showSellingActivities && (
    <div
        style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}
        onClick={() => setShowSellingActivities(false)}
    >
       <div
    style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        width: '500px',
        maxWidth: '90vw',
        maxHeight: '75vh',
        overflowY: 'auto',
        boxSizing: 'border-box',
        lineHeight: 1.5,
        fontSize: '14px'
    }}
    onClick={e => e.stopPropagation()}
>
    <h3 style={{marginTop: 0}}>
        Creating & Selling SB3 Activities
    </h3>

    <p style={{marginTop: 0}}>
        When selling an SB3 activity, choose how buyers can access your
        project. This is especially important if your project contains code
        you want to protect.
    </p>

    <strong>STANDARD — DOWNLOADABLE</strong>

    <p>
        Standard SB3 products can be downloaded by the buyer. This is useful
        when you want buyers to have their own copy of the SB3 file that they
        can open, edit, or customize.
    </p>

    <p>
        <strong>Important:</strong> Because the buyer receives the original
        SB3 file, they can open it in a compatible editor and view any code
        included in the project.
    </p>

    <strong>ENCRYPTED — PROTECTED</strong>

    <p>
        Encrypted SB3 products cannot be downloaded. Buyers access and use the
        activity through Alyd Market, helping protect the original project and
        any code created by the seller.
    </p>

    <p>
        Encrypted is a good choice when your activity contains code or other
        work that you do not want distributed as an original SB3 file.
    </p>

    <strong>CODE BUILDING WITH PURCHASED ACTIVITIES</strong>

    <p>
        Alyd checks whether a purchased SB3 already contains code.
    </p>

    <p>
        If the SB3 <strong>contains code</strong>, buyers can use the activity,
        but they cannot view the creator's code or access Code Building for
        that purchased activity.
    </p>

    <p>
        If the SB3 <strong>does not contain code</strong>, buyers can use Code
        Building with the purchased activity. Their changes cannot be saved or
        used to create another product.
    </p>

    <strong>NFT RESALE</strong>

    <p>
        Sellers of Encrypted products can choose whether buyers are allowed to
        wrap their purchase into an NFT for resale. NFT resale is optional and
        is separate from the protection provided by an Encrypted product.
    </p>

    <p>
        If NFT resale is enabled, the seller can receive a royalty when the NFT
        is resold.
    </p>

    <button
        onClick={() => setShowSellingActivities(false)}
        style={{
            padding: '6px 14px',
            cursor: 'pointer'
        }}
    >
        Close
    </button>
</div>
    </div>
)}
  {showAiCreditsBanner && (
  <AiCreditsBanner
    credits={aiCredits}
    freeScratchAssetGenerationsUsed={
        freeScratchAssetGenerationsUsed
    }
    freeScratchSoapNotesUsed={
    freeScratchSoapNotesUsed
}
    freeScratchBackdropsUsed={
        freeScratchBackdropsUsed
    }
    freeScratchProjectsUsed={
        freeScratchProjectsUsed
    }
    onBuyCredits={() => {
        window.location.href =
            'https://alydmarket.com/ai-credits/checkout';
    }}
    onClose={() => setShowAiCreditsBanner(false)}
/>
)}
        
  
   
{therapyLoading && isTherapyMode ? (
    <div
        style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(20, 24, 35, 0.68)'
        }}
    >
        <div
            style={{
                width: '440px',
                maxWidth: 'calc(100vw - 40px)',
                padding: '32px',
                borderRadius: '18px',
                background: '#ffffff',
                boxShadow:
                    '0 18px 60px rgba(0, 0, 0, 0.28)'
            }}
        >
            <h2
                style={{
                    marginTop: 0,
                    marginBottom: '8px',
                    fontSize: '24px'
                }}
            >
                Creating your therapy activity
            </h2>

            <p
                style={{
                    marginTop: 0,
                    marginBottom: '28px',
                    color: '#666666',
                    lineHeight: 1.4
                }}
            >
                Alyd is preparing everything for your session.
            </p>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '18px'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '16px',
                        opacity:
                            therapyBuildStage === 'planning' ||
                            therapyBuildStage === 'assets' ||
                            therapyBuildStage === 'building' ?
                                1 :
                                0.4
                    }}
                >
                    <div
                        style={{
                            width: '28px',
                            height: '28px',
                            flexShrink: 0,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            background:
                                therapyBuildStage === 'assets' ||
                                therapyBuildStage === 'building' ?
                                    '#e5f7eb' :
                                    '#e8f0ff'
                        }}
                    >
                        {therapyBuildStage === 'assets' ||
                        therapyBuildStage === 'building' ?
                            '✓' :
                            '●'}
                    </div>

                    <span>Planning your activity</span>
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '16px',
                        opacity:
                            therapyBuildStage === 'assets' ||
                            therapyBuildStage === 'building' ?
                                1 :
                                0.4
                    }}
                >
                    <div
                        style={{
                            width: '28px',
                            height: '28px',
                            flexShrink: 0,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            background:
                                therapyBuildStage === 'building' ?
                                    '#e5f7eb' :
                                    therapyBuildStage === 'assets' ?
                                        '#e8f0ff' :
                                        '#eeeeee'
                        }}
                    >
                        {therapyBuildStage === 'building' ?
                            '✓' :
                            therapyBuildStage === 'assets' ?
                                '●' :
                                '○'}
                    </div>

                    <span>Creating the scene</span>
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '16px',
                        opacity:
                            therapyBuildStage === 'assets' ||
                            therapyBuildStage === 'building' ?
                                1 :
                                0.4
                    }}
                >
                    <div
                        style={{
                            width: '28px',
                            height: '28px',
                            flexShrink: 0,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            background:
                                therapyBuildStage === 'building' ?
                                    '#e5f7eb' :
                                    therapyBuildStage === 'assets' ?
                                        '#e8f0ff' :
                                        '#eeeeee'
                        }}
                    >
                        {therapyBuildStage === 'building' ?
                            '✓' :
                            therapyBuildStage === 'assets' ?
                                '●' :
                                '○'}
                    </div>

                    <span>Creating interactive items</span>
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '16px',
                        opacity:
                            therapyBuildStage === 'building' ?
                                1 :
                                0.4
                    }}
                >
                    <div
                        style={{
                            width: '28px',
                            height: '28px',
                            flexShrink: 0,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            background:
                                therapyBuildStage === 'building' ?
                                    '#e8f0ff' :
                                    '#eeeeee'
                        }}
                    >
                        {therapyBuildStage === 'building' ?
                            '●' :
                            '○'}
                    </div>

                    <span>Building your activity</span>
                </div>
            </div>

            <p
                style={{
                    marginTop: '28px',
                    marginBottom: 0,
                    color: '#888888',
                    fontSize: '13px'
                }}
            >
                This may take a moment while Alyd creates your custom assets.
            </p>
        </div>
    </div>
) : null}

{isTherapyMode ? (
    <Box
        className={boxStyles}
        style={{
            display: 'flex',
            flexDirection:
                !therapyStudentView && isNarrowTherapy ?
                    'column' :
                    'row',
            flex: 1,
            minHeight: 0,
            padding: '12px',
            gap: '12px',
            background: '#f7f7f7'
        }}
    >
       {/* TEMP deployment marker */}


{/* Narrow-screen Clinician toggle */}
{!therapyStudentView && isNarrowTherapy && (
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    flexShrink: 0
                }}
            >
                <button
                    type="button"
                    onClick={() => setMobileClinicianTools(false)}
                    style={{
                        padding: '7px 14px',
                        cursor: 'pointer'
                    }}
                >
                    Activity
                </button>

                <button
                    type="button"
                    onClick={() => setMobileClinicianTools(true)}
                    style={{
                        padding: '7px 14px',
                        cursor: 'pointer'
                    }}
                >
                    Clinician Tools
                </button>
            </div>
        )}

        {/* Activity / Stage */}
        {!(!therapyStudentView && isNarrowTherapy && mobileClinicianTools) && (
            <Box
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center'
                }}
            >
                <Box
                    style={{
                        flex: '0 1 auto',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: therapyStudentView ? 'flex-start' : 'center',
                        justifyContent: 'center',
                        background: 'F7F7F7',
                        borderRadius: '12px',
                        padding: '12px',
                        boxSizing: 'border-box',
                        // marginTop: therapyStudentView ? '4px' : '4px'
                    }}
                >
                    <StageWrapper
                        onToggleStageDragging={() => {
                            setAllowStageDragging(current => !current);
                        }}
                        isPurchasedCodeBuilding={isPurchasedCodeBuilding}
                        onTogglePurchasedCodeBuilding={() => {
                            setIsTherapyMode(false);
                            setIsPurchasedCodeBuilding(true);
                        }}
                        isTherapyMode={isTherapyMode}
                        allowStageDragging={allowStageDragging}
                        isFullScreen={isFullScreen}
                        isViewerMode={isViewerMode}
                        isRendererSupported={isRendererSupported}
                        isRtl={isRtl}
                        isCreating={isCreating}
                        stageSize={
                            therapyStudentView ?
                                studentStageSize :
                                STAGE_DISPLAY_SIZES.therapy
                        }
                        vm={vm}
                        ariaRole="region"
                        ariaLabel={intl.formatMessage(ariaMessages.stage)}
                        manuallySaveThumbnails={manuallySaveThumbnails}
                        onSetManualThumbnail={onSetManualThumbnail}
                        onSetManualThumbnailButtonClick={onSetManualThumbnailButtonClick}
                        loading={loading}
                        showNewFeatureCallouts={showNewFeatureCallouts}
                        userOwnsProject={userOwnsProject}
                        username={username}
                        onUpdateProjectThumbnail={onUpdateProjectThumbnail}
                    />
                </Box>
            </Box>
        )}


{!therapyStudentView &&
    (!isNarrowTherapy || mobileClinicianTools) && (
        <Box
               style={{
        width: isNarrowTherapy ? '600px' : '280px',
        maxWidth: '100%',
        minWidth: isNarrowTherapy ? 0 : '280px',
        alignSelf: isNarrowTherapy ? 'center' : 'stretch',
        flexShrink: 1,
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxSizing: 'border-box',
        overflowY: 'auto',
        maxHeight: isNarrowTherapy ? 'calc(100vh - 180px)' : 'none',
    }}
        >
            {!therapyActivityStarted ? (
                <>
                    <h2
                        style={{
                            marginTop: 0,
                            marginBottom: '8px'
                        }}
                    >
                        Therapy
                    </h2>

                    <p
                        style={{
                            marginTop: 0,
                            marginBottom: '24px'
                        }}
                    >
                        Create an activity for this session.
                    </p>

                  <div>
    <strong>Age</strong>

    <div
        style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginTop: '8px',
            marginBottom: '16px'
        }}
    >
        {[
            ['3-5', '3–5'],
            ['6-8', '6–8'],
            ['9-12', '9–12'],
            ['13-17', '13–17'],
            ['adult', 'Adult']
        ].map(([value, label]) => (
            <button
                key={value}
                type="button"
                style={{
                    padding: '7px 10px',
                    borderRadius: '8px',
                    border:
                        therapyAgeRange === value ?
                            '2px solid #855cd6' :
                            '1px solid #cccccc',
                    background:
                        therapyAgeRange === value ?
                            '#eee8ff' :
                            'white',
                    cursor: 'pointer'
                }}
                onClick={() => {
                    setTherapyAgeRange(value);
                }}
            >
                {label}
            </button>
        ))}
    </div>

    <strong>Interactive items</strong>

    <div
        style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginTop: '8px',
            marginBottom: '16px'
        }}
    >
        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(count => (
            <button
                key={count}
                type="button"
                style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '8px',
                    border:
                        therapyItemCount === count ?
                            '2px solid #855cd6' :
                            '1px solid #cccccc',
                    background:
                        therapyItemCount === count ?
                            '#eee8ff' :
                            'white',
                    cursor: 'pointer'
                }}
                onClick={() => {
                    setTherapyItemCount(count);
                }}
            >
                {count}
            </button>
        ))}
    </div>

    <strong>Difficulty</strong>

    <div
        style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginTop: '8px',
            marginBottom: '16px'
        }}
    >
        {[
            ['auto', 'Auto'],
            ['easy', 'Easy'],
            ['medium', 'Medium'],
            ['difficult', 'Difficult']
        ].map(([value, label]) => (
            <button
                key={value}
                type="button"
                style={{
                    padding: '7px 10px',
                    borderRadius: '8px',
                    border:
                        therapyDifficulty === value ?
                            '2px solid #855cd6' :
                            '1px solid #cccccc',
                    background:
                        therapyDifficulty === value ?
                            '#eee8ff' :
                            'white',
                    cursor: 'pointer'
                }}
                onClick={() => {
                    setTherapyDifficulty(value);
                }}
            >
                {label}
            </button>
        ))}
    </div>

    <strong>Rounds</strong>

    <div
        style={{
            display: 'flex',
            gap: '6px',
            marginTop: '8px',
            marginBottom: '16px'
        }}
    >
        {[1, 2, 3, 4, 5].map(count => (
            <button
                key={count}
                type="button"
                style={{
                    width: '38px',
                    height: '34px',
                    borderRadius: '8px',
                    border:
                        therapyRoundCount === count ?
                            '2px solid #855cd6' :
                            '1px solid #cccccc',
                    background:
                        therapyRoundCount === count ?
                            '#eee8ff' :
                            'white',
                    cursor: 'pointer'
                }}
                onClick={() => {
                    setTherapyRoundCount(count);
                }}
            >
                {count}
            </button>
        ))}
    </div>

    <strong>Client interests</strong>

    <div
    style={{
        fontSize: '12px',
        color: '#666',
        marginTop: '4px',
        marginBottom: '8px'
    }}
>
    The more details you provide, the better your activity will be.
</div>

    <input
        type="text"
        value={therapyInterests}
        placeholder="Halloween, dinosaurs, basketball..."
        style={{
            width: '100%',
            marginTop: '8px',
            marginBottom: '16px',
            padding: '10px',
            boxSizing: 'border-box',
            borderRadius: '8px',
            border: '1px solid #cccccc'
        }}
        onChange={event => {
            setTherapyInterests(event.target.value);
        }}
        onKeyDown={event => event.stopPropagation()}
        onKeyUp={event => event.stopPropagation()}
    />

    <strong>What are you working on?</strong>

<div
    style={{
        fontSize: '12px',
        color: '#666',
        marginTop: '4px',
        marginBottom: '8px'
    }}
>
    The more details you provide, the better your activity will be.
</div>

    <textarea
        value={therapyPrompt}
        placeholder="/s/ and /z/ discrimination and production"
        style={{
            width: '100%',
            minHeight: '100px',
            marginTop: '8px',
            padding: '10px',
            boxSizing: 'border-box',
            resize: 'vertical',
            borderRadius: '8px',
            border: '1px solid #cccccc'
        }}
        onChange={event => {
            setTherapyPrompt(event.target.value);
        }}
        onKeyDown={event => event.stopPropagation()}
        onKeyUp={event => event.stopPropagation()}
    />
</div>
{therapyError ? (
    <div
        style={{
            marginTop: '12px',
            fontSize: '14px'
        }}
    >
        {therapyError}
    </div>
) : null}

{/* <button
    type="button"
    disabled={therapyLoading || !therapyPrompt.trim()}
    style={{
        width: '100%',
        marginTop: '16px',
        padding: '12px',
        border: 'none',
        borderRadius: '8px',
        cursor:
            therapyLoading || !therapyPrompt.trim() ?
                'default' :
                'pointer',
        fontWeight: 'bold'
    }}
    onClick={async () => {
        const cleanPrompt = therapyPrompt.trim();

        if (!cleanPrompt) return;

        setTherapyLoading(true);
        setTherapyError('');
        setTherapyBuildStage('planning');

        try {
            const functions = getFunctions(
                getApp(),
                'us-central1'
            );

            const generateTherapyActivityPlan = httpsCallable(
                functions,
                'generateTherapyActivityPlan'
            );

            const result = await generateTherapyActivityPlan({
                prompt: cleanPrompt,
                ageRange: therapyAgeRange,
                itemCount: therapyItemCount,
                difficulty: therapyDifficulty,
                roundCount: therapyRoundCount,
                interests: therapyInterests.trim()
            });

            const activity = result.data?.activity;

            if (
                !activity ||
                !activity.title ||
                !activity.goal ||
                !activity.backdropPrompt ||
                !Array.isArray(activity.rounds) ||
                activity.rounds.length !== therapyRoundCount ||
                !Array.isArray(activity.sprites) ||
                activity.sprites.length !== therapyItemCount
            ) {
                throw new Error(
                    'The activity returned by AI was incomplete.'
                );
            }

            const spritePlans = Array.isArray(activity.sprites) ?
                activity.sprites :
                [];

            if (spritePlans.length === 0) {
                throw new Error(
                    'The activity did not include any usable sprites.'
                );
            }

            setTherapyBuildStage('assets');

            const generateTherapyAssets = httpsCallable(
                functions,
                'generateTherapyAssets'
            );

            console.log(
                '[THERAPY] Requesting therapy assets:',
                {
                    backdropPrompt: activity.backdropPrompt,
                    sprites: spritePlans.map(sprite => sprite.name)
                }
            );

          const assetBatchSize = 4;

const spriteBatches = [];

for (
    let i = 0;
    i < spritePlans.length;
    i += assetBatchSize
) {
    spriteBatches.push(
        spritePlans.slice(
            i,
            i + assetBatchSize
        )
    );
}
console.log(
    '[THERAPY] Requesting therapy assets:',
    {
        backdropPrompt: activity.backdropPrompt,
        sprites: spritePlans.map(sprite => sprite.name)
    }
);

const assetResult = await generateTherapyAssets({
    backdropPrompt: activity.backdropPrompt,
    sprites: spritePlans.map(sprite => ({
        name: sprite.name,
        prompt: sprite.prompt
    }))
});

const backdropImageBase64 = assetResult.data?.backdropImageBase64;
const generatedAssetSprites = assetResult.data?.sprites || [];

if (!backdropImageBase64) {
    throw new Error(
        'No backdrop image was returned.'
    );
}

if (
    !Array.isArray(generatedAssetSprites) ||
    generatedAssetSprites.length === 0
) {
    throw new Error(
        'No therapy sprites were returned.'
    );
}

console.log(
    '[THERAPY] Therapy assets returned:',
    {
        backdrop: Boolean(backdropImageBase64),
        spriteCount: generatedAssetSprites.length
    }
);

setTherapyBuildStage('building');

const storage = vm.runtime.storage;

const vmCostumes = await prepareAIBackdrop(
    backdropImageBase64,
    storage
);

const stageTarget = vm.runtime.getTargetForStage();

if (!stageTarget) {
    throw new Error(
        'Could not find the Scratch stage.'
    );
}

vm.setEditingTarget(stageTarget.id);

await Promise.all(
    vmCostumes.map(backdrop =>
        vm.addBackdrop(
            backdrop.md5,
            backdrop
        )
    )
);

console.log(
    '[THERAPY] AI backdrop added.'
);

const generatedSprites = await Promise.all(
    generatedAssetSprites.map(async (generatedAsset, i) => {
        if (!generatedAsset?.imageBase64) {
            throw new Error(
                `No image was returned for ${
                    generatedAsset?.name ||
                    `sprite ${i + 1}`
                }.`
            );
        }

        console.log(
            `[THERAPY] Preparing sprite ${i + 1} of ${generatedAssetSprites.length}:`,
            generatedAsset.name || 'AI Sprite'
        );

        const newSprite = await prepareAISprite(
            generatedAsset.imageBase64,
            storage,
            generatedAsset.name ||
                `AI Sprite ${i + 1}`
        );

        console.log(
            `[THERAPY] Sprite ${i + 1} prepared:`,
            generatedAsset.name || 'AI Sprite'
        );

        return {
            name:
                generatedAsset.name ||
                `AI Sprite ${i + 1}`,
            sprite: newSprite
        };
    })
);

if (generatedSprites.length === 0) {
    throw new Error(
        'No usable sprites were generated.'
    );
}

const existingSprites = vm.runtime.targets.filter(
    target => !target.isStage
);

console.log(
    '[THERAPY] Existing sprites:',
    existingSprites.map(target => ({
        id: target.id,
        name: target.getName()
    }))
);

await Promise.all(
    existingSprites.map(target =>
        Promise.resolve(vm.deleteSprite(target.id))
    )
);

console.log(
    '[THERAPY] Existing sprites deleted. Targets now:',
    vm.runtime.targets.map(target => ({
        id: target.id,
        name: target.getName(),
        isStage: target.isStage
    }))
);
            const spriteCount = generatedSprites.length;

let spriteSize;
let columns;

if (spriteCount <= 3) {
    spriteSize = 95;
    columns = spriteCount;
} else if (spriteCount <= 4) {
    spriteSize = 82;
    columns = 2;
} else if (spriteCount <= 6) {
    spriteSize = 72;
    columns = 3;
} else if (spriteCount <= 8) {
    spriteSize = 60;
    columns = 4;
} else {
    spriteSize = 52;
    columns = 5;
}

const rows = Math.ceil(spriteCount / columns);

const xSpacing = {
    1: 0,
    2: 180,
    3: 150,
    4: 115,
    5: 90
}[columns];

const ySpacing = rows > 1 ? 160 : 0;

const spritePositions = [];

for (let row = 0; row < rows; row++) {
    const startIndex = row * columns;

    const itemsInRow = Math.min(
        columns,
        spriteCount - startIndex
    );

    const rowWidth =
        (itemsInRow - 1) * xSpacing;

    const startX =
        -(rowWidth / 2);

    const y =
        rows === 1 ?
            0 :
            ((rows - 1) * ySpacing) / 2 -
                (row * ySpacing);

    for (let column = 0; column < itemsInRow; column++) {
        spritePositions.push({
            x: startX + (column * xSpacing),
            y
        });
    }
}

console.log(
    '[THERAPY] Sprite layout:',
    {
        spriteCount,
        spriteSize,
        columns,
        rows,
        positions: spritePositions
    }
);

for (
    let i = 0;
    i < generatedSprites.length;
    i++
) {
    const generatedSprite =
        generatedSprites[i];

    console.log(
        `[THERAPY] Adding sprite ${i + 1} of ${generatedSprites.length}:`,
        generatedSprite.name
    );

    await vm.addSprite(
        generatedSprite.sprite
    );

    const addedSprites =
        vm.runtime.targets.filter(
            target => !target.isStage
        );

    const addedSprite =
        addedSprites[addedSprites.length - 1];

    if (addedSprite) {
        const position =
            spritePositions[i];

        addedSprite.setXY(
            position.x,
            position.y
        );

        addedSprite.setSize(
            spriteSize
        );

        console.log(
            '[THERAPY] Sprite positioned and sized:',
            generatedSprite.name,
            {
                position,
                size: spriteSize
            }
        );
    }

    console.log(
        '[THERAPY] AI sprite added:',
        generatedSprite.name
    );
}

            console.log(
                '[THERAPY] All AI sprites added. Targets now:',
                vm.runtime.targets.map(target => ({
                    id: target.id,
                    name: target.getName(),
                    isStage: target.isStage
                }))
            );

            setTherapyActivity(activity);
            setTherapyStep(0);

            setTherapyRoundData(
                activity.rounds.map(() => ({
                    plus: 0,
                    minus: 0,
                    note: ''
                }))
            );

            setTherapyActivityStarted(true);
        } catch (err) {
            console.error(
                'Therapy activity generation error:',
                err
            );

            setTherapyError(
                err.message ||
                'Could not create the activity.'
            );
        } finally {
            setTherapyLoading(false);
            setTherapyBuildStage('');
        }
    }}
>
    {therapyLoading ?
        'Creating Activity...' :
        'Create Activity'}
</button> */}
<button
    type="button"
    disabled={therapyLoading || !therapyPrompt.trim()}
    style={{
        width: '100%',
        marginTop: '16px',
        padding: '12px',
        border: 'none',
        borderRadius: '8px',
        cursor:
            therapyLoading || !therapyPrompt.trim() ?
                'default' :
                'pointer',
        fontWeight: 'bold'
    }}
    onClick={() => {
        setShowTherapyProjectCreator(true);
    }}
>
    {therapyLoading ?
        'Creating Activity...' :
        'Create Activity'}
</button>


                </>
            ) : (
                <>
                 <h2
    style={{
        marginTop: 0,
        marginBottom: '6px'
    }}
>
    {therapyActivity?.title || 'Therapy Activity'}
</h2>

{therapyActivity?.goal ? (
    <p
        style={{
            marginTop: 0,
            marginBottom: '12px',
            opacity: 0.7
        }}
    >
        {therapyActivity.goal}
    </p>
) : null}

<p
    style={{
        marginTop: 0,
        marginBottom: '6px',
        opacity: 0.7
    }}
>
    Round {therapyStep + 1} of {
        therapyActivity?.rounds?.length || 0
    }
</p>

<h3
    style={{
        marginTop: 0,
        marginBottom: '12px'
    }}
>
    {therapyActivity?.rounds?.[therapyStep]?.title || ''}
</h3>

<div
    style={{
        marginTop: '18px',
        padding: '18px',
        borderRadius: '10px',
        background: '#f7f7f7',
        fontSize: '18px',
        lineHeight: 1.4
    }}
>
    {therapyActivity?.rounds?.[therapyStep]?.instruction}
</div>

{therapyActivity?.rounds?.[therapyStep]?.childAction ? (
    <div
        style={{
            marginTop: '14px',
            fontSize: '14px',
            lineHeight: 1.4,
            opacity: 0.75
        }}
    >
        <strong>Client action: </strong>
        {therapyActivity.rounds[therapyStep].childAction}
    </div>
) : null}

{therapyActivity?.rounds?.[therapyStep]?.successCriterion ? (
    <div
        style={{
            marginTop: '12px',
            padding: '10px 12px',
            borderRadius: '8px',
            background: '#f0ecff',
            fontSize: '14px',
            lineHeight: 1.4
        }}
    >
        <strong>Score + when: </strong>
        {
            therapyActivity
                .rounds[therapyStep]
                .successCriterion
        }
    </div>
) : null}

{therapyActivity?.rounds?.[therapyStep]?.trials ? (
    <div
        style={{
            marginTop: '10px',
            fontSize: '13px',
            opacity: 0.65
        }}
    >
        <strong>Suggested opportunities: </strong>
        {therapyActivity.rounds[therapyStep].trials}
    </div>
) : null}

<div
    style={{
        marginTop: '20px',
        padding: '16px',
        borderRadius: '12px',
        border: '1px solid #e1e1e1',
        background: '#ffffff'
    }}
>
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '14px'
        }}
    >
        <strong>Round score</strong>

        <span
            style={{
                fontSize: '20px',
                fontWeight: 700
            }}
        >
            {
                (
                    (therapyRoundData[therapyStep]?.plus || 0) +
                    (therapyRoundData[therapyStep]?.minus || 0)
                ) > 0 ?
                    `${therapyRoundData[therapyStep]?.plus || 0}/${
                        (therapyRoundData[therapyStep]?.plus || 0) +
                        (therapyRoundData[therapyStep]?.minus || 0)
                    }` :
                    '—'
            }
        </span>
    </div>

    <div
        style={{
            display: 'flex',
            gap: '12px'
        }}
    >
        <button
            type="button"
            style={{
                flex: 1,
                minHeight: '62px',
                borderRadius: '12px',
                border: '1px solid #cccccc',
                background: '#f4fff7',
                cursor: 'pointer',
                fontSize: '24px',
                fontWeight: 700
            }}
            onClick={() => {
                setTherapyRoundData(current =>
                    current.map((roundData, index) =>
                        index === therapyStep ?
                            {
                                ...roundData,
                                plus: roundData.plus + 1
                            } :
                            roundData
                    )
                );
            }}
        >
            + {therapyRoundData[therapyStep]?.plus || 0}
        </button>

        <button
            type="button"
            style={{
                flex: 1,
                minHeight: '62px',
                borderRadius: '12px',
                border: '1px solid #cccccc',
                background: '#fff7f7',
                cursor: 'pointer',
                fontSize: '24px',
                fontWeight: 700
            }}
            onClick={() => {
                setTherapyRoundData(current =>
                    current.map((roundData, index) =>
                        index === therapyStep ?
                            {
                                ...roundData,
                                minus: roundData.minus + 1
                            } :
                            roundData
                    )
                );
            }}
        >
            − {therapyRoundData[therapyStep]?.minus || 0}
        </button>
    </div>

  <div
    style={{
        marginTop: '14px',
        marginBottom: '5px',
        color: '#666666',
        fontSize: '12px',
        lineHeight: '1.3'
    }}
>
    This note will be used for the S — Subjective section of the SOAP note.
</div>

<input
    type="text"
    value={
        therapyRoundData[therapyStep]?.note || ''
    }
    placeholder="Quick note: modeled, repeated, min cues..."
    style={{
        width: '100%',
        padding: '10px',
        boxSizing: 'border-box',
        borderRadius: '8px',
        border: '1px solid #cccccc'
    }}
    onChange={event => {
        const value = event.target.value;

        setTherapyRoundData(current =>
            current.map((roundData, index) =>
                index === therapyStep ?
                    {
                        ...roundData,
                        note: value
                    } :
                    roundData
            )
        );
    }}
    onKeyDown={event => event.stopPropagation()}
    onKeyUp={event => event.stopPropagation()}
/>
</div>

<div
    style={{
        marginTop: '18px',
        padding: '14px',
        borderRadius: '10px',
        background: '#f7f7f7'
    }}
>
    <strong
        style={{
            display: 'block',
            marginBottom: '10px'
        }}
    >
        Session rounds
    </strong>

    {therapyRoundData.map((roundData, index) => {
        const total =
            roundData.plus +
            roundData.minus;

        return (
            <div
                key={index}
                style={{
                    padding: '8px 0',
                    borderTop:
                        index === 0 ?
                            'none' :
                            '1px solid #e5e5e5'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '10px'
                    }}
                >
                    <strong>
                        R{index + 1}
                    </strong>

                    <span>
                        {total > 0 ?
                            `${roundData.plus}/${total}` :
                            '—'}
                    </span>
                </div>

                {roundData.note ? (
                    <div
                        style={{
                            marginTop: '4px',
                            fontSize: '12px',
                            lineHeight: 1.3,
                            opacity: 0.7
                        }}
                    >
                        {roundData.note}
                    </div>
                ) : null}
            </div>
        );
    })}
</div>

<div
    style={{
        display: 'flex',
        gap: '10px',
        marginTop: '20px'
    }}
>
    <button
        type="button"
        disabled={therapyStep === 0}
        style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            cursor:
                therapyStep === 0 ?
                    'default' :
                    'pointer'
        }}
        onClick={() => {
            setTherapyStep(currentStep =>
                Math.max(
                    0,
                    currentStep - 1
                )
            );
        }}
    >
        Previous
    </button>

    <button
        type="button"
        disabled={
            therapyStep >=
            (therapyActivity?.rounds?.length || 1) - 1
        }
        style={{
            flex: 1,
            padding: '10px',
            borderRadius: '8px',
            cursor:
                therapyStep >=
                (therapyActivity?.rounds?.length || 1) - 1 ?
                    'default' :
                    'pointer'
        }}
        onClick={() => {
            setTherapyStep(currentStep =>
                Math.min(
                    (therapyActivity?.rounds?.length || 1) - 1,
                    currentStep + 1
                )
            );
        }}
    >
        Next
    </button>
</div>

{isTherapyMode && !isViewerMode && (
    <button
        type="button"
        style={{
            width: '100%',
            marginTop: '10px',
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer'
        }}
        onClick={() => {
            setTherapyRoundData([]);
            setTherapyStep(0);
            setTherapyActivity(null);
            setTherapyActivityStarted(false);
            setTherapyError('');
        }}
    >
        Create Another Activity
    </button>
)}
<button
    type="button"
    style={{
        width: '100%',
        marginTop: '24px',
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #855cd6',
        background: '#855cd6',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 700
    }}
    onClick={() => {
        setTherapySoapCreditUsage(null);
        setTherapySoapError('');
        setTherapySoapNote('');
        setTherapySoapOpen(true);
    }}
>
    Generate SOAP Note
</button>
</>

            )}
              {showTherapyProjectCreator && (
        <TherapyProjectCreator
            onCreate={handleCreateTherapyActivity}
            onClose={() => {
                setShowTherapyProjectCreator(false);
            }}
        />
     )}
        </Box>
    )}
    </Box>
) : (
    <Box className={classNames(boxStyles, styles.flexWrapper)}>

                        <Box
                            role="main"
                            aria-label={intl.formatMessage(ariaMessages.editor)}
                            className={styles.editorWrapper}
                            element="main"
                        >
                            <Tabs
                                forceRenderTabPanel
                                className={tabClassNames.tabs}
                                selectedIndex={activeTabIndex}
                                selectedTabClassName={tabClassNames.tabSelected}
                                selectedTabPanelClassName={tabClassNames.tabPanelSelected}
                                onSelect={onActivateTab}

                                // TODO: focusTabOnClick should be true for accessibility, but currently conflicts
                                // with nudge operations in the paint editor. We'll likely need to manage focus
                                // differently within the paint editor before we can turn this back on.
                                // Repro steps:
                                // 1. Click the Costumes tab
                                // 2. Select something in the paint editor (say, the cat's face)
                                // 3. Press the left or right arrow key
                                // Desired behavior: the face should nudge left or right
                                // Actual behavior: the Code or Sounds tab is now focused
                                focusTabOnClick={false}
                            >
                                <Box
                                    role="region"
                                    aria-label={intl.formatMessage(ariaMessages.tabList)}
                                >
                                    <TabList
                                        className={tabClassNames.tabList}
                                        role="tablist"
                                    >
                                        <Tab
                                            className={tabClassNames.tab}
                                            tabIndex="0"
                                            role="tab"
                                        >
                                            <img
                                                draggable={false}
                                                src={codeIcon}
                                            />
                                            <FormattedMessage
                                                defaultMessage="Code"
                                                description="Button to get to the code panel"
                                                id="gui.gui.codeTab"
                                            />
                                        </Tab>
                                        <Tab
                                            className={tabClassNames.tab}
                                            onClick={onActivateCostumesTab}
                                            role="tab"
                                            tabIndex="0"
                                        >
                                            <img
                                                draggable={false}
                                                src={costumesIcon}
                                            />
                                            {targetIsStage ? (
                                                <FormattedMessage
                                                    defaultMessage="Backdrops"
                                                    description="Button to get to the backdrops panel"
                                                    id="gui.gui.backdropsTab"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Costumes"
                                                    description="Button to get to the costumes panel"
                                                    id="gui.gui.costumesTab"
                                                />
                                            )}
                                        </Tab>
                                        <Tab
                                            className={tabClassNames.tab}
                                            onClick={onActivateSoundsTab}
                                            role="tab"
                                            tabIndex="0"
                                        >
                                            <img
                                                draggable={false}
                                                src={soundsIcon}
                                            />
                                            <FormattedMessage
                                                defaultMessage="Sounds"
                                                description="Button to get to the sounds panel"
                                                id="gui.gui.soundsTab"
                                            />
                                        </Tab>
                                    </TabList>
                                </Box>
                                <TabPanel
                                    className={tabClassNames.tabPanel}
                                    role="tabpanel"
                                >
                                    <Box
                                        className={styles.blocksWrapper}
                                        role="region"
                                        aria-label={intl.formatMessage(ariaMessages.codePanel)}
                                        element="section"
                                    >
                                        <Blocks
                                            key={`${blocksId}/${colorMode}/${theme}`}
                                            canUseCloud={canUseCloud}
                                            grow={1}
                                            isVisible={blocksTabVisible}
                                            options={{
                                                media: `${basePath}static/${colorModeMap[colorMode].blocksMediaFolder}/`
                                            }}
                                            stageSize={stageSize}
                                            theme={theme}
                                            vm={vm}
                                            colorMode={colorMode}
                                        />
                                    </Box>
                                    <ExtensionsButton
                                        intl={intl}
                                        onExtensionButtonClick={onExtensionButtonClick}
                                    />
                                    <Box className={styles.watermark}>
                                        <Watermark />
                                    </Box>
                                </TabPanel>
                                <TabPanel
                                    className={tabClassNames.tabPanel}
                                    role="tabpanel"
                                >
                                    {costumesTabVisible ? <CostumeTab
                                        ariaLabel={targetIsStage ? intl.formatMessage(ariaMessages.backdropsPanel) :
                                            intl.formatMessage(ariaMessages.costumesPanel)}
                                        ariaRole="region"
                                        vm={vm}
                                        onNewLibraryBackdropClick={onNewLibraryBackdropClick}
                                        onNewLibraryCostumeClick={onNewLibraryCostumeClick}
                                    /> : null}
                                </TabPanel>
                                <TabPanel
                                    className={tabClassNames.tabPanel}
                                    role="tabpanel"
                                >
                                    {soundsTabVisible ?
                                        <SoundTab
                                            ariaLabel={intl.formatMessage(ariaMessages.soundsPanel)}
                                            ariaRole="region"
                                            vm={vm}
                                        /> : null}
                                </TabPanel>
                            </Tabs>
                            {/* {backpackVisible && backpackConfigured ? (
                                <Backpack
                                    host={backpackHost}
                                    ariaRole="region"
                                    ariaLabel={intl.formatMessage(ariaMessages.backpack)}
                                />
                            ) : null} */}
                        </Box>

                        <Box
                            role="complementary"
                            aria-label={intl.formatMessage(ariaMessages.stageAndTarget)}
                            className={classNames(styles.stageAndTargetWrapper, styles[stageSize])}
                            element="aside"
                        >
                            <StageWrapper
                            onToggleStageDragging={() => {
    setAllowStageDragging(current => !current);
}}
                            allowStageDragging={allowStageDragging}
                            isPurchasedCodeBuilding={isPurchasedCodeBuilding}
                            onTogglePurchasedCodeBuilding={() => {
                                setIsPurchasedCodeBuilding(current => !current);
                            }}
                            isFullScreen={isFullScreen}
                            isViewerMode={isViewerMode}
                            isRendererSupported={isRendererSupported}
                            isRtl={isRtl}
                            isCreating={isCreating}
                            stageSize={stageSize}
                            vm={vm}
                            ariaRole="region"
                            ariaLabel={intl.formatMessage(ariaMessages.stage)}
                            manuallySaveThumbnails={manuallySaveThumbnails}
                            onSetManualThumbnail={onSetManualThumbnail}
                            onSetManualThumbnailButtonClick={onSetManualThumbnailButtonClick}
                            loading={loading}
                            showNewFeatureCallouts={showNewFeatureCallouts}
                            userOwnsProject={userOwnsProject}
                            username={username}
                            onUpdateProjectThumbnail={onUpdateProjectThumbnail}
                            />
                            <Box
                                className={styles.targetWrapper}
                                role="region"
                                aria-label={intl.formatMessage(ariaMessages.targetPane)}
                                element="section"
                            >
                                <TargetPane
                                    stageSize={stageSize}
                                    vm={vm}
                                    onNewSpriteClick={onNewSpriteClick}
                                    onNewBackdropClick={onNewLibraryBackdropClick}
                                />
                            </Box>
                                              </Box>
                    </Box>
                )}
                <DragLayer />
                          <DragLayer />

                {therapySoapOpen && (
                   <TherapySoapModal
    note={therapySoapNote}
    creditUsage={therapySoapCreditUsage}
    credits={aiCredits}
    freeScratchSoapNotesUsed={freeScratchSoapNotesUsed}
    loading={therapySoapLoading}
    error={therapySoapError}
    onNoteChange={setTherapySoapNote}
    onGenerate={handleGenerateTherapySoap}
    onCopy={() => {
        navigator.clipboard.writeText(therapySoapNote);
    }}
    onClose={() => {
        setTherapySoapOpen(false);
    }}
/>
                )}
            </Box>
        </ModalFocusProvider>
        );
    }}</MediaQuery>);
};

GUIComponent.propTypes = {
    accountMenuOptions: AccountMenuOptionsPropTypes,
    activeTabIndex: PropTypes.number,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    authorAvatarBadge: PropTypes.number,
    backdropLibraryVisible: PropTypes.bool,
    backpackConfigured: PropTypes.bool,
    backpackHost: PropTypes.string,
    backpackVisible: PropTypes.bool,
    basePath: PropTypes.string,
    blocksTabVisible: PropTypes.bool,
    blocksId: PropTypes.string,
    canChangeLanguage: PropTypes.bool,
    canChangeColorMode: PropTypes.bool,
    canChangeTheme: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    canUseCloud: PropTypes.bool,
    cardsVisible: PropTypes.bool,
    children: PropTypes.node,
    costumeLibraryVisible: PropTypes.bool,
    costumesTabVisible: PropTypes.bool,
    debugModalVisible: PropTypes.bool,
    hasActiveMembership: PropTypes.bool,
    onDebugModalClose: PropTypes.func,
    onTutorialSelect: PropTypes.func,
    enableCommunity: PropTypes.bool,
    isCreating: PropTypes.bool,
    isFetchingUserData: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    isPlayerOnly: PropTypes.bool,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    loading: PropTypes.bool,
    logo: PropTypes.string,
    manuallySaveThumbnails: PropTypes.bool,
    onSetManualThumbnail: PropTypes.func,
    onSetManualThumbnailButtonClick: PropTypes.func,
    isViewerMode: PropTypes.bool,
    menuBarHidden: PropTypes.bool,
    onActivateCostumesTab: PropTypes.func,
    onActivateSoundsTab: PropTypes.func,
    onActivateTab: PropTypes.func,
    onClickLogo: PropTypes.func,
    onExtensionButtonClick: PropTypes.func,
    onLogOut: PropTypes.func,
    onNewSpriteClick: PropTypes.func,
    onNewLibraryCostumeClick: PropTypes.func,
    onClickLogin: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onRequestCloseBackdropLibrary: PropTypes.func,
    onRequestCloseCostumeLibrary: PropTypes.func,
    onRequestCloseDebugModal: PropTypes.func,
    onRequestCloseTelemetryModal: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onShowPrivacyPolicy: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onTabSelect: PropTypes.func,
    onTelemetryModalCancel: PropTypes.func,
    onTelemetryModalOptIn: PropTypes.func,
    onTelemetryModalOptOut: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    onUpdateProjectThumbnail: PropTypes.func,
    platform: PropTypes.oneOf(Object.keys(PLATFORM)),
    renderLogin: PropTypes.func,
    setTheme: PropTypes.func.isRequired,
    showComingSoon: PropTypes.bool,
    showNewFeatureCallouts: PropTypes.bool,
    soundsTabVisible: PropTypes.bool,
    stageSizeMode: PropTypes.oneOf(Object.keys(STAGE_SIZE_MODES)),
    setPlatform: PropTypes.func,
    targetIsStage: PropTypes.bool,
    telemetryModalVisible: PropTypes.bool,
    colorMode: PropTypes.string,
    theme: PropTypes.string,
    tipsLibraryVisible: PropTypes.bool,
    useExternalPeripheralList: PropTypes.bool, // true for CDM, false for normal Scratch Link
    username: PropTypes.string,
    avatarBadge: PropTypes.number,
    userOwnsProject: PropTypes.bool,
    hideTutorialProjects: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};

GUIComponent.defaultProps = {
    backpackHost: null,
    backpackVisible: false,
    basePath: './',
    blocksId: 'original',
    // TODO: Currently all of those are always true. Do we actually need them?
    canChangeLanguage: true,
    canChangeColorMode: true,
    canChangeTheme: true,
    canCreateNew: false,
    canEditTitle: false,
    canManageFiles: true,
    canRemix: false,
    canSave: false,
    canCreateCopy: false,
    canShare: false,
    canUseCloud: false,
    enableCommunity: false,
    isCreating: false,
    isShared: false,
    isTotallyNormal: false,
    loading: false,
    menuBarHidden: false,
    showComingSoon: false,
    showNewFeatureCallouts: false,
    stageSizeMode: STAGE_SIZE_MODES.large,
    useExternalPeripheralList: false,
    isViewerMode: false
};

const mapStateToProps = state => ({
    // This is the button's mode, as opposed to the actual current state
    blocksId: state.scratchGui.timeTravel.year.toString(),
    stageSizeMode: state.scratchGui.stageSize.stageSize,
    colorMode: state.scratchGui.settings.colorMode,
    theme: state.scratchGui.settings.theme,
    backpackConfigured: !!state.scratchGui.config.storage?.backpackStorage
});

const mapDispatchToProps = dispatch => ({
    setPlatform: platform => dispatch(setPlatform(platform)),
    setTheme: theme => dispatch(setTheme(theme)),
    onSetFullScreen: isFullScreen => dispatch(setFullScreen(isFullScreen))
});

export default connect(mapStateToProps,
    mapDispatchToProps)(GUIComponent);
